import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, FileSignature, Loader2, ArrowLeft, Printer, ShieldCheck, Building2 } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { LEGAL_DOC_CLASSES } from '@/components/dashboard/legalDocStyles';

/*
  Mis documentos — documentos firmados por el miembro.
  Hoy: Términos y Condiciones aceptados tras la compra.
  Más adelante: contratos de participación en operaciones, etc.
  (mismo modelo user_documents en el backend, distinto "type").
*/

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const MEMBERSHIP_TYPES = ['terms'];

function DocRow({ d }) {
  return (
    <Link to={`/dashboard/documentos/${d.id}`}
      className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 hover:border-[#c9a84c]/30 transition-colors"
      data-testid="doc-row">
      <div className="flex items-center gap-3 min-w-0">
        <FileSignature size={18} className="text-[#c9a84c] flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-white font-medium truncate">{d.title}</p>
          <p className="text-[11px] text-gray-400">Versión {d.version_label || d.version} · Firmado el {fmtDate(d.signed_at)}</p>
        </div>
      </div>
      <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-2.5 py-1 flex-shrink-0">Firmado</span>
    </Link>
  );
}

function Section({ icon: Icon, title, docs, empty }) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-[11px] text-gray-400 uppercase tracking-wider font-semibold"><Icon size={13} /> {title}</h3>
      {docs.length ? docs.map((d) => <DocRow key={d.id} d={d} />)
        : <p className="rounded-xl border border-dashed border-white/[0.08] px-5 py-6 text-sm text-gray-400">{empty}</p>}
    </section>
  );
}

export function DashboardDocumentos() {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { dashboardApi.myDocuments().then((r) => setDocs(r.documents || [])).catch((e) => setError(e.message)); }, []);

  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!docs) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#c9a84c]" /></div>;

  const membershipDocs = docs.filter((d) => MEMBERSHIP_TYPES.includes(d.type));
  const otherDocs = docs.filter((d) => !MEMBERSHIP_TYPES.includes(d.type));
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 max-w-3xl" data-testid="dashboard-documentos">
      <div>
        <h2 className="text-white text-xl font-semibold">Mis documentos</h2>
        <p className="text-xs text-gray-400 mt-1">Los documentos que has firmado con Freedom Homes Club. Puedes consultarlos y guardarlos en PDF cuando quieras.</p>
      </div>
      <Section icon={FileText} title="Membresía" docs={membershipDocs} empty="Aún no tienes documentos de membresía firmados." />
      <Section icon={Building2} title="Operaciones y otros documentos" docs={otherDocs} empty="Aquí aparecerán tus contratos de participación en operaciones y cualquier otra documentación que firmes con el club." />
    </motion.div>
  );
}

export function DashboardDocumentoDetalle() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { dashboardApi.getDocument(id).then(setDoc).catch((e) => setError(e.message)); }, [id]);

  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!doc) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#c9a84c]" /></div>;
  const s = doc.signer || {};

  return (
    <div className="max-w-3xl space-y-6" data-testid="documento-detalle">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #fhc-printable, #fhc-printable * { visibility: visible !important; color: #000 !important; background: transparent !important; border-color: #999 !important; }
          #fhc-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; }
        }
      `}</style>
      <div className="flex items-center justify-between gap-4 print:hidden">
        <Link to="/dashboard/documentos" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white"><ArrowLeft size={14} /> Mis documentos</Link>
        <button type="button" onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-xl border border-[#c9a84c]/40 px-4 py-2 text-sm text-[#c9a84c] hover:bg-[#c9a84c]/10" data-testid="doc-print">
          <Printer size={15} /> Imprimir / Guardar PDF
        </button>
      </div>

      <div id="fhc-printable" className="space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#c9a84c] font-bold">Freedom Homes Club · Documento firmado</p>
          <h2 className="text-white text-xl font-semibold mt-1">{doc.title}</h2>
          <p className="text-xs text-gray-400 mt-1">Versión {doc.version_label || doc.version}</p>
        </div>

        <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.04] p-5 text-xs text-gray-300 space-y-1.5" data-testid="doc-certificate">
          <p className="flex items-center gap-2 text-emerald-400 font-semibold text-sm mb-2"><ShieldCheck size={16} /> Constancia de aceptación electrónica</p>
          <p><span className="text-gray-400">Firmante:</span> {s.full_name} · {doc.document_type_label} {s.document_number}</p>
          <p><span className="text-gray-400">Dirección fiscal:</span> {s.fiscal_address}, {s.province}, {s.country}</p>
          <p><span className="text-gray-400">Email:</span> {s.email}</p>
          <p><span className="text-gray-400">Fecha y hora:</span> {fmtDate(doc.signed_at)}</p>
          <p><span className="text-gray-400">IP:</span> {doc.ip || '—'} · <span className="text-gray-400">Método:</span> {doc.signature_method}</p>
          <p className="break-all"><span className="text-gray-400">Huella SHA-256 del texto aceptado:</span> {doc.sha256}</p>
          <p><span className="text-gray-400">Referencia:</span> {doc.id}</p>
        </div>

        <div className={`${LEGAL_DOC_CLASSES} rounded-xl border border-white/[0.06] bg-white/[0.02] p-6`} dangerouslySetInnerHTML={{ __html: doc.html }} />
      </div>
    </div>
  );
}

export default DashboardDocumentos;
